import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ArrowLeft, Save, Loader2, FileText, Building2, Gauge, Key, Plus, Trash2,
} from 'lucide-react';

export default function Bill2RollinEditPage() {
  const { accountIdRef } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPropertyId = searchParams.get('propertyId');

  const [currentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('property');

  // Data
  const [prop, setProp] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageRef, setPageRef] = useState(1);
  const [jobId, setJobId] = useState('');
  const [fileName, setFileName] = useState('');

  // Editable forms
  const [propForm, setPropForm] = useState({});
  const [acctForms, setAcctForms] = useState({});
  const [meterForms, setMeterForms] = useState({});
  const [credentials, setCredentials] = useState({ url: '', user_id: '', password: '' });

  const goBack = () => {
    const pid = fromPropertyId || prop?.property_id;
    if (pid) navigate(`/ubm/bill2rollin/property/${pid}`);
    else navigate('/ubm/bill2rollin');
  };

  useEffect(() => {
    if (!accountId || !accountIdRef) return;
    const load = async () => {
      setLoading(true);
      try {
        // Get the account to find its property
        const { data: acct } = await api.get(`/bill2rollin/accounts/${accountIdRef}`, { params: { account_id: accountId } });
        const propertyId = acct.property_id;
        setJobId(acct.job_id || '');
        setFileName(acct.file_name || '');
        setPageRef(acct.page_ref || 1);

        // Get property + all accounts for this property (same bill context)
        const { data } = await api.get(`/bill2rollin/properties/${propertyId}/accounts`, { params: { account_id: accountId } });
        const p = data.property;
        setProp(p);
        setAllAccounts(data.accounts || []);

        // Init property form
        setPropForm({
          name: p.name || '', address: p.address || '', city: p.city || '',
          state: p.state || '', zip: p.zip || '', ein: p.ein || '',
          mysql_site_id: p.mysql_site_id || '',
        });

        // Init account forms keyed by account_id_ref
        const af = {};
        const mf = {};
        for (const a of (data.accounts || [])) {
          af[a.account_id_ref] = {
            account_number: a.account_number || '',
            vendor: a.vendor || '',
            utility_type: a.utility_type || '',
            unit_number: a.unit_number || 'N/A',
            service_address: a.service_address || '',
            state: a.state || '',
            zip: a.zip || '',
            gl_code: a.gl_code || '',
          };
          for (const m of (a.meters || [])) {
            if (m.status === 'deleted') continue;
            mf[m.meter_id] = {
              meter_number: m.meter_number || '',
              meter_identifier: m.meter_identifier || '',
              account_number: a.account_number || '',
              commodity: m.commodity || a.utility_type || '',
              gl_code: m.gl_code || '',
              page_ref: m.page_ref || '',
            };
          }
        }
        setAcctForms(af);
        setMeterForms(mf);

        // Load credentials if saved
        setCredentials(p.credentials || { url: '', user_id: '', password: '' });

        // Load PDF
        if (acct.job_id) {
          setPdfLoading(true);
          try {
            const pdfRes = await api.get(`/bill2rollin/jobs/${acct.job_id}/pdf-url`, { params: { account_id: accountId } });
            setPdfUrl(pdfRes.data.url || '');
          } catch { /* silent */ }
          finally { setPdfLoading(false); }
        }
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    load();
  }, [accountId, accountIdRef]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const propertyId = prop?.property_id;
      // 1. Save property
      await api.patch(`/bill2rollin/properties/${propertyId}?account_id=${accountId}`, {
        ...propForm, credentials,
      });

      // 2. Save all accounts
      for (const [ref, form] of Object.entries(acctForms)) {
        await api.patch(`/bill2rollin/accounts/${ref}?account_id=${accountId}`, form);
      }

      // 3. Save meters (update within accounts)
      for (const [meterId, form] of Object.entries(meterForms)) {
        // Find which account this meter belongs to
        for (const acct of allAccounts) {
          const meter = (acct.meters || []).find(m => m.meter_id === meterId);
          if (meter) {
            // Update the meter fields via account update (meters are nested)
            const updatedMeters = (acct.meters || []).map(m =>
              m.meter_id === meterId ? { ...m, ...form } : m
            );
            await api.patch(`/bill2rollin/accounts/${acct.account_id_ref}?account_id=${accountId}`, {
              meters: updatedMeters,
            });
            break;
          }
        }
      }

      toast.success('All changes saved');
      goBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const utilityAccounts = allAccounts.filter(a => a.unit_number === 'N/A' || !a.unit_number);
  const unitAccounts = allAccounts.filter(a => a.unit_number && a.unit_number !== 'N/A');
  const allMeters = allAccounts.flatMap(a => (a.meters || []).filter(m => m.status !== 'deleted').map(m => ({ ...m, _vendor: a.vendor, _acct_ref: a.account_id_ref })));

  const pdfSrc = pdfUrl ? `${pdfUrl}#page=${pageRef}` : '';

  if (!accountId) return <MainLayout><div className="p-8 text-center text-sm text-muted-foreground">Select an organization.</div></MainLayout>;

  return (
    <MainLayout wide>
      <div className="p-4 md:p-6" data-testid="b2r-edit-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={goBack} data-testid="b2r-edit-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Property
          </Button>
          {prop && (
            <div>
              <h1 className="text-base font-bold flex items-center gap-2">
                Edit: {prop.name}
                <Badge variant="outline" className="text-[9px] h-4 px-1.5">{prop.mysql_status || 'new'}</Badge>
              </h1>
              <p className="text-[11px] text-muted-foreground">
                {fileName} {pageRef > 0 && <span className="text-violet-600 font-mono ml-1">Page {pageRef}</span>}
              </p>
            </div>
          )}
          <div className="ml-auto">
            <Button onClick={handleSaveAll} disabled={saving} className="gap-2" data-testid="b2r-save-all">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save All Changes
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4"><Skeleton className="h-[600px]" /><Skeleton className="h-[600px]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ height: 'calc(100vh - 160px)' }}>
            {/* Left: PDF Viewer */}
            <div className="border rounded-lg overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b bg-card flex items-center gap-2">
                <FileText className="h-4 w-4 text-red-500" />
                <span className="text-xs font-medium">{fileName || 'Bill PDF'}</span>
                {pageRef > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-violet-700 bg-violet-50 border-violet-200">Page {pageRef}</Badge>}
              </div>
              <div className="flex-1 bg-muted/20">
                {pdfLoading ? (
                  <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : pdfSrc ? (
                  <iframe title="Bill PDF" src={pdfSrc} className="w-full h-full border-0" data-testid="b2r-edit-pdf" />
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-muted-foreground">PDF not available</div>
                )}
              </div>
            </div>

            {/* Right: 5-tab edit form */}
            <div className="border rounded-lg overflow-hidden flex flex-col">
              {/* Tabs */}
              <div className="flex border-b bg-card px-2">
                {[
                  { id: 'property', label: 'Property', icon: Building2 },
                  { id: 'utility', label: `Utility (${utilityAccounts.length})`, icon: FileText },
                  { id: 'unit', label: `Unit (${unitAccounts.length})`, icon: FileText },
                  { id: 'meters', label: `Meters (${allMeters.length})`, icon: Gauge },
                  { id: 'credentials', label: 'Credentials', icon: Key },
                ].map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)}
                    className={`px-3 py-2 text-[11px] font-medium border-b-2 transition-colors flex items-center gap-1 ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    data-testid={`edit-tab-${t.id}`}>
                    <t.icon className="h-3 w-3" />{t.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {/* Property Tab */}
                {tab === 'property' && (
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">Property / Entity Details</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2"><Label className="text-[10px]">Property Name</Label><Input value={propForm.name || ''} onChange={e => setPropForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                      <div className="col-span-2"><Label className="text-[10px]">Address</Label><Input value={propForm.address || ''} onChange={e => setPropForm(f => ({ ...f, address: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                      <div><Label className="text-[10px]">City</Label><Input value={propForm.city || ''} onChange={e => setPropForm(f => ({ ...f, city: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                      <div><Label className="text-[10px]">State</Label><Input value={propForm.state || ''} onChange={e => setPropForm(f => ({ ...f, state: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                      <div><Label className="text-[10px]">Zip</Label><Input value={propForm.zip || ''} onChange={e => setPropForm(f => ({ ...f, zip: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                      <div><Label className="text-[10px]">EIN (Tax ID)</Label><Input value={propForm.ein || ''} onChange={e => setPropForm(f => ({ ...f, ein: e.target.value }))} className="h-8 text-xs mt-1 font-mono" placeholder="XX-XXXXXXX" /></div>
                      <div className="col-span-2"><Label className="text-[10px]">MySQL Site ID</Label><Input value={propForm.mysql_site_id || ''} onChange={e => setPropForm(f => ({ ...f, mysql_site_id: e.target.value }))} className="h-8 text-xs mt-1 font-mono" /></div>
                    </div>
                  </div>
                )}

                {/* Utility Accounts Tab */}
                {tab === 'utility' && (
                  <div className="space-y-4">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">Utility Accounts (Common Area) - {utilityAccounts.length}</h3>
                    {utilityAccounts.length === 0 ? <p className="text-xs text-muted-foreground">No common area accounts.</p> :
                      utilityAccounts.map((a, i) => (
                        <div key={a.account_id_ref} className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
                          <div className="text-[10px] font-semibold text-muted-foreground uppercase">Account #{i + 1} {a.page_ref && <span className="text-violet-600 font-mono ml-1">p.{a.page_ref}</span>}</div>
                          <div className="grid grid-cols-3 gap-2">
                            <div><Label className="text-[10px]">Account #</Label><Input value={acctForms[a.account_id_ref]?.account_number || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], account_number: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                            <div><Label className="text-[10px]">Vendor</Label><Input value={acctForms[a.account_id_ref]?.vendor || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], vendor: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div><Label className="text-[10px]">Utility Type</Label><Input value={acctForms[a.account_id_ref]?.utility_type || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], utility_type: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div className="col-span-2"><Label className="text-[10px]">Service Address</Label><Input value={acctForms[a.account_id_ref]?.service_address || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], service_address: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div><Label className="text-[10px]">GL Code</Label><Input value={acctForms[a.account_id_ref]?.gl_code || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], gl_code: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}

                {/* Unit Accounts Tab */}
                {tab === 'unit' && (
                  <div className="space-y-4">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">Unit Accounts - {unitAccounts.length}</h3>
                    {unitAccounts.length === 0 ? <p className="text-xs text-muted-foreground">No unit accounts.</p> :
                      unitAccounts.map((a, i) => (
                        <div key={a.account_id_ref} className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
                          <div className="text-[10px] font-semibold text-muted-foreground uppercase">Account #{i + 1} {a.page_ref && <span className="text-violet-600 font-mono ml-1">p.{a.page_ref}</span>}</div>
                          <div className="grid grid-cols-3 gap-2">
                            <div><Label className="text-[10px]">Account #</Label><Input value={acctForms[a.account_id_ref]?.account_number || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], account_number: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                            <div><Label className="text-[10px]">Vendor</Label><Input value={acctForms[a.account_id_ref]?.vendor || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], vendor: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div><Label className="text-[10px]">Unit Number</Label><Input value={acctForms[a.account_id_ref]?.unit_number || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], unit_number: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div className="col-span-2"><Label className="text-[10px]">Service Address</Label><Input value={acctForms[a.account_id_ref]?.service_address || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], service_address: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div><Label className="text-[10px]">Utility Type</Label><Input value={acctForms[a.account_id_ref]?.utility_type || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], utility_type: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div><Label className="text-[10px]">GL Code</Label><Input value={acctForms[a.account_id_ref]?.gl_code || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], gl_code: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                            <div><Label className="text-[10px]">State</Label><Input value={acctForms[a.account_id_ref]?.state || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], state: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                            <div><Label className="text-[10px]">Zip</Label><Input value={acctForms[a.account_id_ref]?.zip || ''} onChange={e => setAcctForms(f => ({ ...f, [a.account_id_ref]: { ...f[a.account_id_ref], zip: e.target.value } }))} className="h-7 text-xs mt-0.5" /></div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}

                {/* Meters Tab */}
                {tab === 'meters' && (
                  <div className="space-y-4">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">Meters - {allMeters.length}</h3>
                    {allMeters.length === 0 ? <p className="text-xs text-muted-foreground">No meters.</p> :
                      allMeters.map((m, i) => (
                        <div key={m.meter_id} className="border rounded-lg p-3 bg-slate-50/50 space-y-2">
                          <div className="text-[10px] font-semibold text-muted-foreground uppercase flex items-center gap-2">
                            <Gauge className="h-3 w-3" /> Meter #{i + 1}
                            {m.page_ref && <span className="text-violet-600 font-mono">p.{m.page_ref}</span>}
                            <span className="text-slate-400 font-normal">({m._vendor})</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div><Label className="text-[10px]">Meter Number</Label><Input value={meterForms[m.meter_id]?.meter_number || ''} onChange={e => setMeterForms(f => ({ ...f, [m.meter_id]: { ...f[m.meter_id], meter_number: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                            <div><Label className="text-[10px]">Meter Identifier</Label><Input value={meterForms[m.meter_id]?.meter_identifier || ''} onChange={e => setMeterForms(f => ({ ...f, [m.meter_id]: { ...f[m.meter_id], meter_identifier: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                            <div><Label className="text-[10px]">Account #</Label><Input value={meterForms[m.meter_id]?.account_number || ''} onChange={e => setMeterForms(f => ({ ...f, [m.meter_id]: { ...f[m.meter_id], account_number: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                            <div><Label className="text-[10px]">Commodity</Label><Input value={meterForms[m.meter_id]?.commodity || ''} onChange={e => setMeterForms(f => ({ ...f, [m.meter_id]: { ...f[m.meter_id], commodity: e.target.value } }))} className="h-7 text-xs mt-0.5" placeholder="electricity, gas, water..." /></div>
                            <div><Label className="text-[10px]">GL Code</Label><Input value={meterForms[m.meter_id]?.gl_code || ''} onChange={e => setMeterForms(f => ({ ...f, [m.meter_id]: { ...f[m.meter_id], gl_code: e.target.value } }))} className="h-7 text-xs mt-0.5 font-mono" /></div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}

                {/* Credentials Tab */}
                {tab === 'credentials' && (
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase text-muted-foreground">Portal Credentials</h3>
                    <p className="text-[10px] text-muted-foreground">Store login credentials for the utility vendor portal associated with this property.</p>
                    <div className="border rounded-lg p-4 space-y-3 bg-slate-50/50">
                      <div><Label className="text-[10px]">Portal URL</Label><Input value={credentials.url || ''} onChange={e => setCredentials(c => ({ ...c, url: e.target.value }))} className="h-8 text-xs mt-1" placeholder="https://vendor-portal.com/login" /></div>
                      <div><Label className="text-[10px]">User ID / Email</Label><Input value={credentials.user_id || ''} onChange={e => setCredentials(c => ({ ...c, user_id: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                      <div><Label className="text-[10px]">Password</Label><Input type="password" value={credentials.password || ''} onChange={e => setCredentials(c => ({ ...c, password: e.target.value }))} className="h-8 text-xs mt-1" /></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Save */}
              <div className="px-4 py-3 border-t bg-card flex items-center gap-2">
                <Button onClick={handleSaveAll} disabled={saving} size="sm" className="gap-2" data-testid="b2r-save-all-bottom">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save All
                </Button>
                <Button variant="outline" size="sm" onClick={goBack}>Cancel</Button>
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {allAccounts.length} accounts, {allMeters.length} meters
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
